import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "vc-call-root" }
const _hoisted_2 = { class: "vc-incoming-hero" }
const _hoisted_3 = { class: "vc-incoming-hero__title" }
const _hoisted_4 = { class: "vc-incoming-hero__connecting" }
const _hoisted_5 = { class: "mb-16" }
const _hoisted_6 = { class: "vc-video-stage" }
const _hoisted_7 = { class: "vc-main-video" }
const _hoisted_8 = { class: "vc-name-chip vc-name-chip--remote" }
const _hoisted_9 = { class: "vc-pip-video" }
const _hoisted_10 = {
  key: 0,
  class: "vc-caption-panel"
}
const _hoisted_11 = { class: "vc-caption-text" }
const _hoisted_12 = {
  key: 1,
  class: "vc-audio-enable-overlay"
}
const _hoisted_13 = { class: "vc-bottom-bar" }
const _hoisted_14 = { class: "vc-bottom-bar__inner" }
const _hoisted_15 = {
  key: 1,
  class: "vc-bottom-bar__spacer"
}
const _hoisted_16 = { class: "vc-device-controls" }
const _hoisted_17 = { class: "vc-call-duration" }

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useCallStore } from '../stores/callStore'
import { useVideoClient, getZoomClientInstance } from '@virtual-care/shared'
import type { Session } from '@virtual-care/shared'
import { apiClient } from '../services/apiClient'
import { signalingService } from '../services/signalingService'
import VideoDisplay from '../components/VideoDisplay.vue'
import CaptionLanguageDialog from '../components/CaptionLanguageDialog.vue'
import PatientBackground from '../components/PatientBackground.vue'

interface Props {
  sessionId: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ActiveCallView',
  props: {
    sessionId: {}
  },
  setup(__props: any) {

const props = __props
const router = useRouter()
const callStore = useCallStore()

const roomId = apiClient.getRoomId()

// Reactive state for handling page refresh
const isRestoringSession = ref(false)
const sessionRestoreError = ref<string | null>(null)
const wasRestoredFromRefresh = ref(false)

// Initialize provider and credentials (may be null on refresh)
// Use env variable as fallback for provider on page refresh
// NOTE: in-room-endpoint uses Vue CLI, so use process.env.VUE_APP_* (not import.meta.env.VITE_*)
const provider = ref(callStore.provider || process.env.VUE_APP_VIDEO_PROVIDER)
const credentials = ref(callStore.credentials)

// Validate provider is set
if (!provider.value) {
  console.error('[ActiveCallView] No video provider configured in store or environment')
  throw new Error('Video provider not configured. Check VUE_APP_VIDEO_PROVIDER environment variable.')
}

const {
  localStream,
  remoteStream,
  isConnected,
  error: videoError,
  answerCall,
  endCall,
  toggleAudio: toggleAudioTrack,
  toggleVideo: toggleVideoTrack,
  // Caption controls from composable
  captionsEnabled: captionsEnabledFromClient,
  currentCaption,
  supportsCaptions,
  availableCaptionLanguages,
  enableCaptions,
  disableCaptions,
  toggleCaptions,
} = useVideoClient(
  props.sessionId,
  roomId,
  'patient',
  provider.value as never,
  credentials.value as never,
  signalingService
)

const showError = ref(false)
const showCaptionDialog = ref(false)
const captionLanguage = ref('en')
const isEnding = ref(false)
const startTime = ref<number | null>(null)
const callDuration = ref('00:00')
let durationInterval: ReturnType<typeof setInterval> | null = null

// Recording state (read-only for patient)
const isRecording = ref(false)

// Audio enable state (for page refresh scenarios)
const needsAudioEnable = ref(false)

// Use caption state from composable (override local if needed)
const captionsEnabled = computed({
  get: () => captionsEnabledFromClient.value,
  set: (value: boolean) => {
    // Control via composable methods
    if (value && !captionsEnabledFromClient.value) {
      enableCaptions(captionLanguage.value)
    } else if (!value && captionsEnabledFromClient.value) {
      disableCaptions()
    }
  }
})

const nurseName = computed(() => callStore.nurseName ?? 'Virtual Nurse')

const captionLanguageLabel = computed(() => {
  const labels: Record<string, string> = {
    'en': 'English',
    'es': 'Spanish',
    'de': 'German',
    'fr': 'French',
    'pt': 'Portuguese',
    'ru': 'Russian',
    'ja': 'Japanese',
    'zh': 'Chinese',
    'ko': 'Korean',
    'it': 'Italian',
    'nl': 'Dutch',
    'ar': 'Arabic',
    'off': 'Off',
  }
  return labels[captionLanguage.value] ?? 'English'
})

const errorMessage = computed(() =>
  videoError.value ?? callStore.error?.message ?? 'An error occurred.'
)

const handleCallEnded = () => {
  if (isEnding.value) return
  isEnding.value = true
  cleanup()
  callStore.endCall()
  router.push('/')
}

function cleanup() {
  if (durationInterval) { clearInterval(durationInterval); durationInterval = null }
  endCall()
}

function startDurationTimer() {
  startTime.value = Date.now()
  durationInterval = setInterval(() => {
    if (startTime.value) {
      const elapsed = Math.floor((Date.now() - startTime.value) / 1000)
      const m = Math.floor(elapsed / 60).toString().padStart(2, '0')
      const s = (elapsed % 60).toString().padStart(2, '0')
      callDuration.value = `${m}:${s}`
    }
  }, 1000)
}

onMounted(async () => {
  // Handle page refresh: restore session if provider not set
  if (!callStore.provider) {
    console.log('[ActiveCallView] No provider in store - restoring session from API')
    isRestoringSession.value = true
    
    try {
      // Join/re-join the session to get credentials
      // If already joined, backend now returns existing credentials
      const joinResponse = await apiClient.joinSession(props.sessionId, 'patient')
      console.log('[ActiveCallView] Join/rejoin response:', joinResponse)
      console.log('[ActiveCallView] Session data:', JSON.stringify(joinResponse.session, null, 2))
      
      if (!joinResponse.session?.provider || !joinResponse.session?.credentials) {
        console.error('[ActiveCallView] Missing fields - provider:', joinResponse.session?.provider, 'credentials:', joinResponse.session?.credentials)
        throw new Error('Invalid session response - missing provider or credentials')
      }
      
      // Populate the call store
      callStore.acceptCall(
        joinResponse.session.provider as never,
        joinResponse.session.credentials as never
      )
      
      // Restore nurse name if available
      if (joinResponse.session.initiatorName) {
        callStore.nurseName = joinResponse.session.initiatorName
      }
      
      // Update reactive refs (provider should already be set from env, but update credentials)
      credentials.value = callStore.credentials
      
      console.log('[ActiveCallView] Session restored successfully, provider:', provider.value, 'has credentials:', !!credentials.value)
      wasRestoredFromRefresh.value = true
      
    } catch (error) {
      console.error('[ActiveCallView] Failed to restore session on refresh:', error)
      sessionRestoreError.value = 'Failed to reconnect to call. Please return to the home screen.'
      
      // Redirect to home after a delay
      setTimeout(() => {
        router.push('/')
      }, 3000)
      return
    } finally {
      isRestoringSession.value = false
    }
  }
  
  // Join the session room to receive call_ended events
  signalingService.joinSession(props.sessionId)
  signalingService.on('call_ended', handleCallEnded)

  try {
    callStore.setStatus('connecting')
    // Pass credentials explicitly in case they were restored on page refresh
    await answerCall(credentials.value)
    callStore.setStatus('connected')
    startDurationTimer()

    // Set up recording event listeners (only for Zoom provider) - read-only for patient
    if (provider.value === 'zoom') {
      try {
        const zoomClient = getZoomClientInstance()
        
        zoomClient.on('recording-started', () => {
          console.log('[ActiveCallView] Recording started')
          isRecording.value = true
        })
        
        zoomClient.on('recording-stopped', () => {
          console.log('[ActiveCallView] Recording stopped')
          isRecording.value = false
        })
        
        console.log('[ActiveCallView] Recording event listeners registered')
        
        // Check if we restored from page refresh - if so, show explicit audio enable prompt
        if (wasRestoredFromRefresh.value) {
          needsAudioEnable.value = true
          console.log('[ActiveCallView] Session was restored from refresh - audio enable prompt will be shown')
        }
        
      } catch (err) {
        console.warn('[ActiveCallView] Failed to set up recording listeners:', err)
      }
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to establish connection'
    console.error('[ActiveCallView] answerCall failed:', message)
    callStore.setError('CALL_FAILED', message)
    showError.value = true
  }
})

onBeforeUnmount(() => {
  signalingService.off('call_ended', handleCallEnded)
  if (!isEnding.value) cleanup()
})

function handleToggleAudio() { toggleAudioTrack(); callStore.toggleAudio() }
function handleToggleVideo() { toggleVideoTrack(); callStore.toggleVideo() }

async function handleLanguageSelected(code: string) {
  try {
    captionLanguage.value = code
    if (code === 'off') {
      await disableCaptions()
    } else {
      await enableCaptions(code)
    }
  } catch (err) {
    console.error('[ActiveCallView] Failed to change caption language:', err)
    const message = err instanceof Error ? err.message : 'Failed to change caption language'
    alert(message)
  }
}

async function handleTurnOffCaptions() {
  try {
    await disableCaptions()
    captionLanguage.value = 'off'
  } catch (err) {
    console.error('[ActiveCallView] Failed to disable captions:', err)
  }
}

async function handleEnableAudio() {
  try {
    console.log('[ActiveCallView] User clicked Enable Audio button')
    const zoomClient = getZoomClientInstance()
    await zoomClient.unmuteAudio()
    console.log('[ActiveCallView] Audio started successfully')
    needsAudioEnable.value = false
  } catch (err) {
    console.error('[ActiveCallView] Failed to enable audio:', err)
    alert('Failed to enable audio. Please try again.')
  }
}

async function handleEndCall() {
  if (isEnding.value) return
  isEnding.value = true
  try {
    await apiClient.endSession(props.sessionId)
    signalingService.notifyCallEnded(props.sessionId, roomId)
  } catch (err) {
    console.error('[ActiveCallView] Error ending session:', err)
  } finally {
    cleanup()
    callStore.endCall()
    router.push('/')
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_VBtnPrimary = _resolveComponent("VBtnPrimary")!
  const _component_VBtnLight = _resolveComponent("VBtnLight")!
  const _component_VBtnDark = _resolveComponent("VBtnDark")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!_unref(isConnected))
      ? (_openBlock(), _createBlock(_component_v_container, {
          key: 0,
          fluid: "",
          class: "pa-0 fill-height bg-backgroundBase vc-incoming-root vc-tv-canvas-bg"
        }, {
          default: _withCtx(() => [
            _createVNode(PatientBackground),
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("h1", _hoisted_3, " Connecting to " + _toDisplayString(nurseName.value) + "... ", 1),
              _createElementVNode("div", _hoisted_4, [
                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "vc-incoming-hero__connecting-text" }, "Please wait a moment", -1)),
                _createVNode(_component_v_progress_circular, {
                  color: "white",
                  indeterminate: "",
                  size: "48",
                  width: "4"
                })
              ])
            ]),
            _createVNode(_component_v_sheet, {
              class: "vc-connecting-panel d-flex flex-column bg-white",
              elevation: "24",
              height: "100%",
              rounded: "0"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_component_v_icon, {
                    class: "vc-rail__video-icon",
                    color: "primary"
                  }, {
                    default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                      _createTextVNode("mdi-video-plus-outline", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "flex-grow-1" }, null, -1))
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (_unref(isConnected))
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createVNode(VideoDisplay, { stream: _unref(remoteStream) }, null, 8, ["stream"]),
              _createElementVNode("div", _hoisted_8, _toDisplayString(nurseName.value), 1),
              (isRecording.value)
                ? (_openBlock(), _createBlock(_component_v_chip, {
                    key: 0,
                    class: "recording-indicator",
                    color: "error",
                    variant: "flat"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        start: "",
                        size: "small"
                      }, {
                        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                          _createTextVNode("mdi-record-circle", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[8] || (_cache[8] = _createTextVNode(" Recording ", -1))
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(VideoDisplay, {
                stream: _unref(localStream),
                mirrored: true,
                muted: true
              }, null, 8, ["stream"]),
              _cache[9] || (_cache[9] = _createElementVNode("div", { class: "vc-name-chip vc-name-chip--local" }, "You", -1))
            ])
          ]),
          (captionsEnabled.value && _unref(currentCaption))
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(currentCaption)), 1)
              ]))
            : _createCommentVNode("", true),
          (needsAudioEnable.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createVNode(_component_v_sheet, {
                  class: "vc-audio-enable-panel d-flex flex-column align-center",
                  elevation: "24",
                  rounded: "xl",
                  "max-width": "600",
                  width: "90%"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      color: "warning",
                      size: "80",
                      class: "mb-4"
                    }, {
                      default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                        _createTextVNode("mdi-volume-high", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[13] || (_cache[13] = _createElementVNode("h2", { class: "text-h4 font-weight-bold text-center mb-4" }, " Enable Audio ", -1)),
                    _cache[14] || (_cache[14] = _createElementVNode("p", { class: "text-body-1 text-center text-medium-emphasis mb-6" }, [
                      _createTextVNode(" Audio was paused when the page refreshed. "),
                      _createElementVNode("br"),
                      _createTextVNode(" Click below to enable two-way audio communication. ")
                    ], -1)),
                    _createVNode(_component_VBtnPrimary, {
                      size: "x-large",
                      class: "text-h6",
                      onClick: handleEnableAudio
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          start: "",
                          size: "large"
                        }, {
                          default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                            _createTextVNode("mdi-volume-high", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[12] || (_cache[12] = _createTextVNode(" Enable Audio ", -1))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, [
              (_unref(supportsCaptions))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (captionsEnabled.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createVNode(_component_VBtnLight, {
                            class: "vc-tray-btn text-none",
                            size: "x-large",
                            onClick: handleTurnOffCaptions
                          }, {
                            default: _withCtx(() => [
                              _cache[16] || (_cache[16] = _createTextVNode(" Turn off closed-captioning ", -1)),
                              _createVNode(_component_v_icon, {
                                end: "",
                                size: "large"
                              }, {
                                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                  _createTextVNode("mdi-close", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_VBtnDark, {
                            class: "vc-tray-btn text-none",
                            size: "x-large",
                            onClick: _cache[0] || (_cache[0] = ($event: any) => (showCaptionDialog.value = true))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(captionLanguageLabel.value) + " ", 1),
                              _createVNode(_component_v_icon, {
                                end: "",
                                size: "large"
                              }, {
                                default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                  _createTextVNode("mdi-chevron-right", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ], 64))
                      : (_openBlock(), _createBlock(_component_VBtnLight, {
                          key: 1,
                          class: "vc-tray-btn text-none",
                          size: "default",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (showCaptionDialog.value = true))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              start: "",
                              size: "small"
                            }, {
                              default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                _createTextVNode("mdi-closed-caption", -1)
                              ]))]),
                              _: 1
                            }),
                            _cache[19] || (_cache[19] = _createTextVNode(" Turn on closed-captioning ", -1))
                          ]),
                          _: 1
                        }))
                  ], 64))
                : _createCommentVNode("", true),
              (_unref(supportsCaptions) && captionsEnabled.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_16, [
                _createVNode(_component_v_btn, {
                  color: _unref(callStore).isAudioEnabled ? 'white' : 'error',
                  icon: "",
                  size: "large",
                  variant: "flat",
                  onClick: handleToggleAudio
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(callStore).isAudioEnabled ? 'mdi-microphone' : 'mdi-microphone-off'), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["color"]),
                _createVNode(_component_v_btn, {
                  color: _unref(callStore).isVideoEnabled ? 'white' : 'error',
                  icon: "",
                  size: "large",
                  variant: "flat",
                  onClick: handleToggleVideo
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(callStore).isVideoEnabled ? 'mdi-video' : 'mdi-video-off'), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["color"]),
                _createElementVNode("span", _hoisted_17, _toDisplayString(callDuration.value), 1),
                _createVNode(_component_v_btn, {
                  color: "error",
                  icon: "",
                  size: "x-large",
                  variant: "flat",
                  onClick: handleEndCall
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                        _createTextVNode("mdi-phone-hangup", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ])
            ])
          ])
        ], 64))
      : _createCommentVNode("", true),
    _createVNode(CaptionLanguageDialog, {
      modelValue: showCaptionDialog.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showCaptionDialog).value = $event)),
      "current-language": captionLanguage.value,
      onLanguageSelected: handleLanguageSelected
    }, null, 8, ["modelValue", "current-language"]),
    _createVNode(_component_v_dialog, {
      modelValue: showError.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showError).value = $event)),
      "max-width": "400",
      persistent: ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, null, {
              default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                _createTextVNode("Call Error", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(errorMessage.value), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_VBtnPrimary, { onClick: handleEndCall }, {
                  default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                    _createTextVNode("End Call", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})